import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TrashIcon, PlayIcon, FilmIcon } from '@heroicons/react/24/outline';
import { IAvatarVideo, AvatarVideoStatus } from '@packages/global';
import MediaPreviewModal from '../common/MediaPreviewModal';

const STATUS_COLORS: Record<string, string> = {
  pending: 'text-gray-400 bg-gray-400/10',
  submitted: 'text-blue-400 bg-blue-400/10',
  processing: 'text-yellow-400 bg-yellow-400/10',
  completed: 'text-green-400 bg-green-400/10',
  failed: 'text-red-400 bg-red-400/10',
};

interface AvatarVideoCardProps {
  item: IAvatarVideo;
  onDelete: (id: string, title: string) => void;
}

export default function AvatarVideoCard({ item, onDelete }: AvatarVideoCardProps) {
  const { t, i18n } = useTranslation(['avatar', 'common']);
  const [showPreview, setShowPreview] = useState(false);
  const title = item.title || item.id.slice(0, 8);

  return (
    <div className="bg-bg-surface border border-border-subtle rounded-2xl overflow-hidden hover:border-primary hover:-translate-y-1 hover:shadow-[0_0_20px_rgba(255,92,0,0.15)] transition-all duration-300">
      {/* Preview area */}
      <div className="relative h-36 bg-bg-elevated flex items-center justify-center">
        {item.resultVideoUrl ? (
          <>
            <video src={item.resultVideoUrl} className="w-full h-full object-cover" muted />
            <button onClick={() => setShowPreview(true)}
              className="absolute inset-0 flex items-center justify-center bg-black/30 opacity-0 hover:opacity-100 transition-opacity cursor-pointer">
              <PlayIcon className="w-10 h-10 text-white" />
            </button>
          </>
        ) : (
          <div className="text-center">
            {item.status === AvatarVideoStatus.PROCESSING || item.status === AvatarVideoStatus.PENDING || item.status === AvatarVideoStatus.SUBMITTED ? (
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary mx-auto" />
            ) : (
              <FilmIcon className="w-10 h-10 text-gray-500" />
            )}
          </div>
        )}
        <span className={`absolute top-2 right-2 text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[item.status] || 'text-gray-400 bg-gray-400/10'}`}>
          {t(`avatar:video.status.${item.status}`)}
        </span>
      </div>

      {/* Info */}
      <div className="p-3">
        <p className="text-gray-50 font-medium text-sm truncate">{title}</p>
        {item.resultMessage && item.status === AvatarVideoStatus.FAILED && (
          <p className="text-red-400 text-xs mt-0.5 line-clamp-1" title={item.resultMessage}>{item.resultMessage}</p>
        )}
        <div className="flex items-center justify-between mt-2">
          <span className="text-gray-500 text-xs">{new Date(item.createdAt).toLocaleDateString(i18n.language)}</span>
          <button onClick={() => onDelete(item.id, item.title || '')}
            className="p-1.5 rounded-lg text-gray-500 hover:text-red-400 hover:bg-red-400/10 transition-colors cursor-pointer">
            <TrashIcon className="w-4 h-4" />
          </button>
        </div>
      </div>

      {item.resultVideoUrl && (
        <MediaPreviewModal isOpen={showPreview} onClose={() => setShowPreview(false)}
          type="video" src={item.resultVideoUrl} alt={title} />
      )}
    </div>
  );
}
